/**
 * Artifacts API
 */

import { Artifact, Chunk } from '../types/api';
import { getAPIClient } from './client';

export interface ListArtifactsParams {
  project_id: string;
  source_id?: string;
  exclude_source_types?: string;
  limit?: number;
  offset?: number;
}

export interface ListArtifactsResponse {
  artifacts: Artifact[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export async function listArtifacts(
  projectId: string,
  limit?: number,
  offset?: number,
  sourceId?: string,
  excludeSourceTypes?: string[],
): Promise<ListArtifactsResponse> {
  const client = getAPIClient();
  const params: ListArtifactsParams = { project_id: projectId };
  if (limit !== undefined) params.limit = limit;
  if (offset !== undefined) params.offset = offset;
  if (sourceId) params.source_id = sourceId;
  if (excludeSourceTypes?.length) params.exclude_source_types = excludeSourceTypes.join(',');

  try {
    const response = await client.get<any>('/artifacts', {
      params,
      timeout: 300000, // 5 minutes for large batches
    });

    // Backend returns a List, not a paginated response
    // Convert to paginated format
    if (Array.isArray(response)) {
      const artifacts = response;
      const hasMore = limit !== undefined && artifacts.length === limit;

      return {
        artifacts,
        total: offset ? offset + artifacts.length : artifacts.length, // Estimate total
        limit: limit || artifacts.length,
        offset: offset || 0,
        has_more: hasMore,
      };
    }

    // If backend ever returns paginated format, use it directly
    return response as ListArtifactsResponse;
  } catch (error: any) {
    // If timeout, return empty response to allow graceful degradation
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn(`[listArtifacts] Request timed out for limit=${limit}, offset=${offset}`);
      return {
        artifacts: [],
        total: 0,
        limit: limit || 0,
        offset: offset || 0,
        has_more: false,
      };
    }
    throw error;
  }
}

export interface SearchArtifactsParams {
  project_id: string;
  query?: string;
  artifact_type?: string;
  source_id?: string;
  source_type?: string;
  exclude_source_types?: string;  // comma-separated list of source_type values to exclude
  metadata_key?: string;
  metadata_value?: string;
  date_from?: string;
  date_to?: string;
  min_size_mb?: number;
  max_size_mb?: number;
  limit?: number;
  offset?: number;
}

export interface SearchArtifactsResponse {
  artifacts: Artifact[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  error?: string;
}

export async function searchArtifacts(params: SearchArtifactsParams): Promise<SearchArtifactsResponse> {
  const client = getAPIClient();
  // Use a longer timeout for artifact search (can be slow with large datasets)
  // Default is 120s, but we'll use 90s for this specific call
  return client.get<SearchArtifactsResponse>('/artifacts/search', {
    params,
    timeout: 90000, // 90 seconds - allows time for large dataset queries
  });
}

export async function getArtifact(artifactId: string): Promise<Artifact> {
  const client = getAPIClient();
  return client.get<Artifact>(`/artifacts/${artifactId}`);
}

export async function uploadArtifact(
  projectId: string,
  file: File,
  onProgress?: (progress: number) => void,
  sourceId?: string,
  sourceType?: string,
  forceReupload?: boolean
): Promise<Artifact> {
  const client = getAPIClient();
  const formData = new FormData();
  formData.append('file', file);

  let url = `/artifacts/upload?project_id=${projectId}`;
  if (sourceId) {
    url += `&source_id=${sourceId}`;
  }
  if (sourceType) {
    url += `&source_type=${encodeURIComponent(sourceType)}`;
  }
  if (forceReupload) {
    url += `&force_reupload=true`;
  }

  return client.upload<Artifact>(
    url,
    formData,
    onProgress
  );
}

export async function downloadArtifact(artifactId: string): Promise<Blob> {
  const client = getAPIClient();
  return client.get<Blob>(`/artifacts/${artifactId}/download`, {
    responseType: 'blob',
  });
}

/**
 * Download an artifact and trigger a browser save, showing a user-friendly toast on failure.
 * Use this everywhere instead of inlining download + alert() logic.
 */
export async function downloadArtifactWithToast(
  artifactId: string,
  filename: string,
  addToast: (toast: { type: 'success' | 'error' | 'warning' | 'info'; title?: string; message?: string; duration?: number }) => void,
): Promise<void> {
  try {
    const blob = await downloadArtifact(artifactId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `document-${artifactId}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error('[downloadArtifactWithToast] Failed:', error);
    addToast({
      type: 'error',
      title: 'Download unavailable',
      message:
        'This file could not be retrieved. It may have been moved, archived, or is no longer accessible on the network drive.',
      duration: 7000,
    });
  }
}

export async function deleteArtifact(artifactId: string): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/artifacts/${artifactId}`);
}

export async function reingestArtifact(artifactId: string): Promise<{ job_id: string }> {
  const client = getAPIClient();
  return client.post<{ job_id: string }>(`/artifacts/${artifactId}/reingest`);
}

/**
 * Get chunks for an artifact
 */
export interface ArtifactChunksResponse {
  artifact_id: string;
  total_chunks: number;
  chunks: Chunk[];
  pagination: {
    offset: number;
    limit: number;
    has_more: boolean;
  };
}

export async function getArtifactChunks(
  artifactId: string,
  filters?: {
    contains_table?: boolean;
    contains_figure?: boolean;
  }
): Promise<ArtifactChunksResponse> {
  const client = getAPIClient();
  const params: Record<string, string> = {};
  if (filters?.contains_table !== undefined) {
    params.contains_table = String(filters.contains_table);
  }
  if (filters?.contains_figure !== undefined) {
    params.contains_figure = String(filters.contains_figure);
  }
  return client.get<ArtifactChunksResponse>(`/artifacts/${artifactId}/chunks`, { params });
}

/**
 * Get artifact statistics
 */
export interface ArtifactStats {
  total_artifacts: number;
  total_size_mb: number;
  by_type: Record<string, number>;
  recent_uploads: number;
  artifacts_with_chunks?: number;
  artifacts_without_chunks?: number;
  total_chunks?: number;
  indexed_chunk_entries?: number;
  by_ingest_status?: Record<string, number>;
}

export async function getArtifactStats(projectId: string): Promise<ArtifactStats> {
  const client = getAPIClient();
  return client.get(`/artifacts/stats`, { params: { project_id: projectId } });
}

/**
 * Batch get chunk counts for multiple artifacts (optimized - uses dedicated backend endpoint)
 */
export async function getChunkCountsBatch(
  projectId: string,
  artifactIds: string[]
): Promise<Record<string, number>> {
  if (artifactIds.length === 0) {
    return {};
  }

  const client = getAPIClient();

  try {
    // Use the new dedicated /artifacts/chunks/counts endpoint which uses GROUP BY for efficiency
    const response = await client.get<{ counts: Record<string, number> }>('/artifacts/chunks/counts', {
      params: {
        project_id: projectId,
        artifact_ids: artifactIds.join(','), // Comma-separated list
      },
    });

    return response.counts || {};
  } catch (error) {
    console.error('[getChunkCountsBatch] Error fetching chunk counts:', error);
    // Return zeros on error
    const counts: Record<string, number> = {};
    artifactIds.forEach(id => {
      counts[id] = 0;
    });
    return counts;
  }
}


